import React, {
	forwardRef,
	useImperativeHandle,
	useState,
	useEffect,
} from "react";
import css from "./TemplateTerms.module.css";
import { FaFilePdf, FaFileWord, FaFileAlt, FaFileUpload } from "react-icons/fa";
import Button from "../../../../../Components/Button/Button";
import { UploadFile, DownloadFile } from "../../../../../Services/Server";
import { toast } from "react-toastify";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Set the worker source for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const TemplateTerms = forwardRef(({ ModalAction, init }, ref) => {
	const [uploadedFile, setUploadedFile] = useState(null);
	const [documentUrl, setDocumentUrl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [numPages, setNumPages] = useState(null);

	const onDocumentLoadSuccess = ({ numPages }) => {
		setNumPages(numPages);
	};

	useEffect(() => {
		if (init && init.doc_id) {
			setLoading(true);
			setError(null);
			DownloadFile(init.doc_id)
				.then((resp) => {
					if (resp instanceof Blob) {
						const url = URL.createObjectURL(resp);
						setDocumentUrl(url);
						setUploadedFile({
							id: init.doc_id,
							name: init.doc_name || "document.pdf",
						});
						setError(null);
					} else {
						throw new TypeError(
							"Invalid file response from server. Expected a Blob."
						);
					}
				})
				.catch((err) => {
					console.error("Error downloading file:", err);
					toast.error("Failed to download document.");
					setError("Failed to download document.");
				})
				.finally(() => {
					setLoading(false);
				});
		} else {
			if (documentUrl) {
				URL.revokeObjectURL(documentUrl);
			}
			setDocumentUrl(null);
			setUploadedFile(null);
			setError(null);
		}

		return () => {
			if (documentUrl) {
				URL.revokeObjectURL(documentUrl);
			}
		};
	}, [init.doc_id]);

	useImperativeHandle(ref, () => ({
		data() {
			return {
				doc_id: uploadedFile ? uploadedFile.id : null,
			};
		},
	}));

	const handleFileChange = (event) => {
		const file = event.target.files[0];
		if (file) {
			setLoading(true);
			setError(null);
			UploadFile(file)
				.then((resp) => {
					setUploadedFile(resp);
					const url = URL.createObjectURL(file);
					setDocumentUrl(url);
					toast.success("File uploaded successfully!");
				})
				.catch((err) => {
					console.error("Error uploading file:", err);
					toast.error("Failed to upload document.");
					setError("Failed to upload document.");
				})
				.finally(() => {
					setLoading(false);
				});
		}
	};

	const handleDragOver = (event) => {
		event.preventDefault();
	};

	const handleDrop = (event) => {
		event.preventDefault();
		const file = event.dataTransfer.files[0];
		if (file) {
			setLoading(true);
			setError(null);
			UploadFile(file)
				.then((resp) => {
					setUploadedFile(resp);
					const url = URL.createObjectURL(file);
					setDocumentUrl(url);
					toast.success("File uploaded successfully!");
				})
				.catch((err) => {
					console.error("Error uploading file:", err);
					toast.error("Failed to upload document.");
					setError("Failed to upload document.");
				})
				.finally(() => {
					setLoading(false);
				});
		}
	};

	const removeFile = () => {
		setUploadedFile(null);
		setError(null);
		if (documentUrl) {
			URL.revokeObjectURL(documentUrl);
			setDocumentUrl(null);
		}
		toast.info("Document removed.");
	};

	const pages = Array.from(new Array(numPages), (el, index) => index + 1);

	return (
		<div className={css.TemplateTerms}>
			<p className={css.Title}>Contract Terms & Conditions</p>
			{documentUrl && (
				<div className={css.documentControls}>
					<div className={css.documentInfo}>
						<FaFilePdf className={css.fileIcon} />
						<p>{uploadedFile?.name || "Document"}</p>
					</div>
					<div className={css.actionButtons}>
						<label
							htmlFor="file-upload"
							className={`${css.replaceButton} Btn-Mid Active`}
						>
							Replace
						</label>
						<Button className="Btn-Mid Inverse" onClick={removeFile}>
							Remove
						</Button>
						<input
							id="file-upload"
							type="file"
							onChange={handleFileChange}
							accept=".pdf"
							style={{ display: "none" }}
						/>
					</div>
				</div>
			)}
			<div className={css.uploadContainer}>
				{loading ? (
					<div className={css.loadingState}>
						<p>Loading document...</p>
					</div>
				) : error ? (
					<div className={css.errorState}>
						<p>{error}</p>
						<Button className="Btn-Mid" onClick={() => setError(null)}>
							Try Again
						</Button>
					</div>
				) : documentUrl ? (
					<div className={css.documentView}>
						<div className={css.documentContent}>
							<Document
								file={documentUrl}
								onLoadSuccess={onDocumentLoadSuccess}
								loading={
									<div className={css.viewerLoading}>Loading document...</div>
								}
								error={
									<div className={css.viewerError}>Failed to load PDF.</div>
								}
							>
								{pages.map((page) => (
									<div key={`page_${page}`} className={css.pdfPage}>
										<Page pageNumber={page} />
									</div>
								))}
							</Document>
						</div>
					</div>
				) : (
					<div
						className={css.dropzone}
						onDragOver={handleDragOver}
						onDrop={handleDrop}
					>
						<input
							id="file-upload"
							type="file"
							onChange={handleFileChange}
							accept=".pdf"
						/>
						<div className={css.centerContent}>
							<FaFileUpload className={css.dropzoneIcon} />
							<p>Choose a file or drag & drop it here</p>
							<p className={css.supportedFormats}>PDF files only, up to 10MB</p>
							<label
								htmlFor="file-upload"
								className={`${css.browseButton} Active`}
							>
								Browse File
							</label>
						</div>
					</div>
				)}
			</div>
		</div>
	);
});

export default TemplateTerms;
